import React, {useState, useCallback} from 'react';
import styles from './CheckCounterpartiesView.module.scss';
import {createBorrowingOnboardingManager} from '../../../../../../../../../di';
import {useErrorCallback} from '../../../../../../../../../hooks';
import {Image, InnAutocomplete} from '../../../../../../../../../components';
import stepApprovedIcon from 'assets/images/svg/step-approved.svg';

const DIGITS_ONLY = /^\d*$/;
const VALID_INN_LENGTHS = [10, 12];
const OGRN_LENGTH = 13;

function isValidInnOrOgrn(value: string): boolean {
  const trimmed = value.trim();
  if (!trimmed) return false;
  if (!DIGITS_ONLY.test(trimmed)) return false;
  const len = trimmed.length;
  return VALID_INN_LENGTHS.includes(len) || len === OGRN_LENGTH;
}

function getValidationError(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (!DIGITS_ONLY.test(trimmed)) {
    return 'Введите только цифры (ИНН или ОГРН)';
  }
  const len = trimmed.length;
  if (VALID_INN_LENGTHS.includes(len) || len === OGRN_LENGTH) return null;
  if (len < Math.min(...VALID_INN_LENGTHS)) {
    return 'ИНН — 10 или 12 цифр, ОГРН — 13 цифр';
  }
  if (len > OGRN_LENGTH) {
    return 'ИНН — не более 12 цифр, ОГРН — 13 цифр';
  }
  return 'ИНН — 10 или 12 цифр, ОГРН — 13 цифр';
}

interface CheckCounterpartiesViewProps {
  onCounterpartyScored?: () => void;
}

const CheckCounterpartiesView = ({onCounterpartyScored}: CheckCounterpartiesViewProps) => {
  const [showForm, setShowForm] = useState(true);
  const [value, setValue] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const handleError = useErrorCallback();

  const trimmed = value.trim();
  const validationError = getValidationError(value);
  const canSubmit =
    trimmed.length > 0 &&
    isValidInnOrOgrn(trimmed) &&
    !validationError &&
    !submitting;

  const handleChange = useCallback((next: string) => {
    if (next !== '' && !DIGITS_ONLY.test(next)) return;
    setValue(next);
    setSubmitError(null);
  }, []);

  const handleOpenForm = useCallback(() => {
    setShowForm(true);
    setValue('');
    setSubmitError(null);
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!canSubmit) return;
      const innOrOgrn = trimmed;
      setSubmitError(null);
      setSubmitting(true);
      try {
        const manager = await createBorrowingOnboardingManager();
        await manager.scoreCounterparty(innOrOgrn);
        setValue('');
        setShowForm(false);
        setShowSuccess(true);
        onCounterpartyScored?.();
      } catch (err: unknown) {
        const message =
          err && typeof err === 'object' && 'message' in err
            ? String((err as { message: unknown }).message)
            : 'Не удалось отправить запрос. Попробуйте позже.';
        setSubmitError(message);
        await handleError(err instanceof Error ? err : new Error(message));
      } finally {
        setSubmitting(false);
      }
    },
    [canSubmit, trimmed, handleError, onCounterpartyScored]
  );

  if (showForm) {
    return (
      <div className={styles.checkCounterpartiesView}>
        <h1 className={styles.title}>Проверка контрагентов</h1>
        <p className={styles.subtitle}>
          Введите данные своего дебитора и получите подробный отчет
        </p>
        <p className={styles.note}>
          Результаты скоринга будут направлены в раздел Уведомления
        </p>

        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          <InnAutocomplete
            type="text"
            inputMode="numeric"
            placeholder="ИНН или ОГРН"
            className={styles.input}
            value={value}
            onChange={handleChange}
            maxLength={13}
            aria-invalid={!!(validationError || submitError)}
            aria-describedby={
              [validationError && 'counterparty-validation-error', submitError && 'counterparty-submit-error']
                .filter(Boolean)
                .join(' ') || undefined
            }
          />
          {validationError && (
            <p id="counterparty-validation-error" className={styles.errorMessage} role="alert">
              {validationError}
            </p>
          )}
          {submitError && (
            <p id="counterparty-submit-error" className={styles.errorMessage} role="alert">
              {submitError}
            </p>
          )}
          <button
            type="submit"
            className={styles.submitButton}
            disabled={!canSubmit}
          >
            {submitting ? 'Отправка…' : 'Отправить'}
          </button>
          <p className={styles.footerNote}>
            Услуга предоставляется компанией Rescore, входящей в группу Кономика
          </p>
        </form>
      </div>
    );
  }

  return (
    <div className={styles.checkCounterpartiesView}>
      <h1 className={styles.title}>Проверка контрагентов</h1>
      <p className={styles.subtitle}>
        Введите данные своего дебитора и получите подробный отчет
      </p>
      <p className={styles.note}>
        Результаты скоринга будут направлены в раздел Уведомления
      </p>

      <div className={styles.notificationCard}>
        {showSuccess && (
          <>
            <div className={styles.notificationHeader}>
              <Image src={stepApprovedIcon} className={styles.notificationIcon} alt="" aria-hidden />
              <div className={styles.notificationText}>
                <p className={styles.notificationTitle}>Заявка на проверку контрагента принята</p>
                <p className={styles.notificationDescription}>
                  Отчет о Вашем дебиторе появится в разделе
                </p>
                <p className={styles.notificationSection}>Уведомления</p>
              </div>
            </div>
          </>
        )}
        {!showSuccess && (
          <p className={styles.notificationIntro}>
            Проверьте контрагента по ИНН или ОГРН — отчет появится в разделе Уведомления
          </p>
        )}
        <button
          type="button"
          className={styles.notificationButton}
          onClick={handleOpenForm}
        >
          Проверить нового контрагента
        </button>
      </div>
    </div>
  );
};

export default CheckCounterpartiesView;
